import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { EmailTemplateEditor } from "./EmailTemplateEditor";
import { MediaLibrary } from "../MediaLibrary";
interface Design {
  preset: "newsletter" | "storm" | "showcase";
  brand: string;
  color: string;
  heading: string;
  message: string;
  image: string;
  imageAlt: string;
  button: string;
  url: string;
}
const defaults: Design = {
  preset: "newsletter",
  brand: "Your roofing company",
  color: "#254b7c",
  heading: "A little care goes a long way",
  message:
    "Hi {{first_name}},\n\nSeasonal maintenance helps protect your home. Let’s plan your next roof inspection.",
  image: "",
  imageAlt: "",
  button: "Schedule an inspection",
  url: "https://example.com",
};
const presets = [
  {
    key: "newsletter",
    name: "Roof care newsletter",
    heading: "A little care goes a long way",
    color: "#254b7c",
  },
  {
    key: "storm",
    name: "Storm response",
    heading: "Here when the storm clears",
    color: "#a04a11",
  },
  {
    key: "showcase",
    name: "Project showcase",
    heading: "A roof built for what comes next",
    color: "#166454",
  },
] as const;
const escape = (text: string) =>
  text.replace(
    /[&<>"']/g,
    (char) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        char
      ]!,
  );
export const safeEmailUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    return ["https:", "http:"].includes(parsed.protocol) ? parsed.href : "";
  } catch {
    return "";
  }
};
export function emailLayout(design: Design) {
  const color = /^#[0-9a-f]{6}$/i.test(design.color) ? design.color : "#254b7c";
  const image = safeEmailUrl(design.image);
  const link = safeEmailUrl(design.url);
  const banner =
    design.preset === "storm"
      ? "ROOFING SUPPORT"
      : design.preset === "showcase"
        ? "PROJECT SPOTLIGHT"
        : "HOMEOWNER NOTES";
  return `<!--apex-email-design:${encodeURIComponent(JSON.stringify(design))}--><table role="presentation" style="width:100%;max-width:640px;margin:auto;border-collapse:collapse;background:#ffffff"><tr><td style="padding:20px 28px;border-bottom:4px solid ${color};font-family:Arial,sans-serif;font-weight:bold;color:${color}">${escape(design.brand)}</td></tr><tr><td style="padding:32px 28px;${design.preset === "storm" ? `background:${color};color:white;` : "background:#f1f5f9;color:#172033;"}"><p style="font-size:11px;letter-spacing:2px">${banner}</p><h1 style="font-size:30px;line-height:1.2;margin:12px 0">${escape(design.heading)}</h1></td></tr>${image ? `<tr><td><img src="${escape(image)}" alt="${escape(design.imageAlt)}" style="width:100%;display:block;height:auto"></td></tr>` : ""}<tr><td style="padding:28px;font-family:Arial,sans-serif;font-size:16px;line-height:1.6;color:#334155">${design.message
    .split("\n\n")
    .map((paragraph) => `<p>${escape(paragraph).replaceAll("\n", "<br>")}</p>`)
    .join(
      "",
    )}${link ? `<p style="margin-top:28px"><a href="${escape(link)}" style="display:inline-block;background:${color};color:white;padding:14px 22px;border-radius:${design.preset === "showcase" ? "2" : "8"}px;text-decoration:none;font-weight:bold">${escape(design.button)}</a></p>` : ""}</td></tr></table>`;
}
function readDesign(html: string): Design | null {
  try {
    const found = html.match(/<!--apex-email-design:(.*?)-->/);
    return found
      ? { ...defaults, ...JSON.parse(decodeURIComponent(found[1])) }
      : null;
  } catch {
    return null;
  }
}
export function EmailDesignWorkbench({
  value,
  onChange,
}: {
  value: string;
  onChange: (html: string) => void;
}) {
  const [mode, setMode] = useState(
    readDesign(value)
      ? "layout"
      : /<table|<html|<body/i.test(value)
        ? "html"
        : "text",
  );
  const [design, setDesign] = useState<Design>(readDesign(value) || defaults);
  const [picker, setPicker] = useState(false);
  const change = (patch: Partial<Design>) => {
    const next = { ...design, ...patch };
    setDesign(next);
    onChange(emailLayout(next));
  };
  const selectPreset = (preset: (typeof presets)[number]) => {
    if (
      value &&
      value !== "<p></p>" &&
      !window.confirm("Replace this template body with the selected layout?")
    )
      return;
    const next = {
      ...defaults,
      preset: preset.key,
      heading: preset.heading,
      color: preset.color,
    };
    setDesign(next);
    onChange(emailLayout(next));
    setMode("layout");
  };
  return (
    <Stack
      spacing={2}
      sx={{
        minWidth: 0,
        "& .MuiFormControl-root": { minWidth: 0 },
        "& .MuiInput-root": { minWidth: 0 },
      }}
    >
      <Typography level="title-md">Start with a layout</Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3,minmax(0,1fr))" },
          gap: 1,
        }}
      >
        {presets.map((preset) => (
          <Card
            key={preset.key}
            sx={{
              borderTop: "5px solid",
              borderTopColor: preset.color,
              p: 1.5,
            }}
          >
            <Typography level="title-sm">{preset.name}</Typography>
            <Typography level="body-xs">{preset.heading}</Typography>
            <Button
              size="sm"
              variant="outlined"
              onClick={() => selectPreset(preset)}
            >
              Use layout
            </Button>
          </Card>
        ))}
      </Box>
      <Select
        aria-label="Body editor"
        value={mode}
        onChange={(_, next) => {
          if (
            next === "text" &&
            mode !== "text" &&
            !window.confirm(
              "Switch to the simple text editor? Complex layout markup may be simplified when edited.",
            )
          )
            return;
          setMode(next || "html");
        }}
      >
        <Option value="text">Rich text</Option>
        <Option value="html">HTML source</Option>
        {readDesign(value) && <Option value="layout">Layout settings</Option>}
      </Select>
      {mode === "layout" && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(0,1fr) minmax(0,1fr)",
              },
              gap: 1,
            }}
          >
            {[
              ["Brand name", "brand"],
              ["Brand color (hex)", "color"],
              ["Heading", "heading"],
              ["Button label", "button"],
              ["Button URL", "url"],
              ["Image alt text", "imageAlt"],
            ].map(([label, key]) => (
              <FormControl key={key}>
                <FormLabel>{label}</FormLabel>
                <Input
                  value={design[key as keyof Design]}
                  onChange={(event) => change({ [key]: event.target.value })}
                />
              </FormControl>
            ))}
          </Box>
          <FormControl>
            <FormLabel>Message</FormLabel>
            <Textarea
              minRows={6}
              value={design.message}
              onChange={(event) => change({ message: event.target.value })}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Image URL</FormLabel>
            <Input
              value={design.image}
              onChange={(event) => change({ image: event.target.value })}
            />
          </FormControl>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => setPicker(true)}>
              Choose library image
            </Button>
            {design.image && (
              <Button
                variant="plain"
                onClick={() => change({ image: "", imageAlt: "" })}
              >
                Remove image
              </Button>
            )}
          </Stack>
          {(!safeEmailUrl(design.url) ||
            design.url === "https://example.com") && (
            <Alert color="warning">
              Set your campaign’s destination URL before sending. Unsupported
              URL schemes are excluded from the rendered button.
            </Alert>
          )}
        </>
      )}
      {mode === "text" && (
        <EmailTemplateEditor value={value} onChange={onChange} />
      )}
      {mode === "html" && (
        <FormControl>
          <FormLabel>Email HTML</FormLabel>
          <Textarea
            minRows={12}
            value={value}
            onChange={(event) => onChange(event.target.value)}
          />
        </FormControl>
      )}
      <Modal open={picker} onClose={() => setPicker(false)}>
        <ModalDialog
          sx={{
            width: 1000,
            maxWidth: "95vw",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Typography level="h4">Choose an email image</Typography>
          <Button variant="plain" onClick={() => setPicker(false)}>
            Close
          </Button>
          <MediaLibrary
            selectionMode
            onSelect={(media) => {
              change({
                image: media.file,
                imageAlt: media.alt_text || media.name,
              });
              setPicker(false);
            }}
          />
        </ModalDialog>
      </Modal>
    </Stack>
  );
}
